import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Card } from "@astryxdesign/core/Card";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Spinner } from "@astryxdesign/core/Spinner";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { MediaTheme } from "@astryxdesign/core/theme";
import { useContainerReveal } from "@astryxdesign/core/hooks";
import { AlertTriangle, Expand, Maximize2, Pause, Play, Shrink, Square, Volume2, VolumeX, X } from "lucide-react";
import type { Camera, StreamStatus } from "../../lib/api";
import { useVideoFit } from "../../lib/useVideoFit";
import { describePlayerState } from "./playerCopy";
import { applyServerHealth, describeServerHealth } from "./serverHealth";
import { useHlsStream } from "./useHlsStream";
import type { StreamSlots } from "./streamSlots";
import type { TileCommands } from "./tileCommands";
import { ptzOverlayStyle, tileLayer, tileLayerContentStyle, TILE_HEADER_CLEARANCE } from "./tileChrome";
import { StreamContentionDialog } from "./StreamContentionDialog";

interface VideoTileProps {
  camera: Camera;
  slots: StreamSlots;
  enabled: boolean;
  /** Start without being asked, because this camera is already being pulled
   *  for somebody else. See useHlsStream's autoStart. */
  autoStart?: boolean;
  /** Live health snapshot from the SSE channel, if one has arrived yet. */
  health?: StreamStatus;
  /** Whether the health feed itself is currently live
   *  (`health.phase === "live"` from useHealthEvents) — passed separately
   *  from `health` because a StreamStatus held from before an interruption
   *  is the last thing that was true, not necessarily what's true now. See
   *  serverHealth.ts's own comment on why this gates every use of `health`
   *  below rather than `health`'s mere presence. */
  isFeedLive?: boolean;
  /** Told when the operator starts or stops this tile by hand, so the page can
   *  stop auto-starting a camera they deliberately turned off. */
  onManualPlay?: () => void;
  onManualStop?: () => void;
  /** Whether this operator may stop somebody else's stream to make room.
   *  False for a viewer, who is told the wall is full but offered no
   *  eviction — that act needs cameras.manage. */
  canEvict?: boolean;
  /** Channel for one-shot orders from the page: somebody stopped this
   *  camera's stream, or a slot request for it was approved. Subscribed to
   *  by camera id rather than watched as a prop value — an order only means
   *  anything to a tile that is mounted and enabled at the moment it
   *  arrives, which a value sitting in a prop cannot express (see
   *  tileCommands.ts for what that shape used to get wrong). Omitted by
   *  surfaces that never receive orders — a detail view with no shared
   *  budget, say. */
  commands?: TileCommands;
  /** Records that a request was sent for this camera, so the dialog can say so
   *  and the page can match the answer when it arrives. */
  onRequestSlot?: (evictCameraId: string) => Promise<void>;
  /** True while a request for this camera is waiting for an answer. */
  isSlotRequested?: boolean;
  /** Slot-budget key, defaults to `camera.id`. Exactly one mounted VideoTile
   *  may use a given tileId at a time — see streamSlots.ts's acquire/release
   *  semantics. The focus-mode dialog passes a distinct constant tileId so
   *  it never fights the grid tile for the same slot. */
  tileId?: string;
  /** Shows a "view fullscreen" affordance in the header when provided.
   *  Omitted by the focus dialog's own tile, which has no need to focus
   *  itself further. */
  onOpenFocus?: () => void;
  /** Shows a "remove from wall" affordance in the header when provided —
   *  distinct from Stop, which only pauses this pane and leaves it holding
   *  its place. This un-ticks it, freeing the spot entirely. Omitted by the
   *  focus dialog's own tile, which is not a wall member to remove. */
  onRemoveFromWall?: () => void;
  /** Rendered over the video, clear of the bottom bar, only while the stream
   *  is live. A slot rather than a PtzPad prop so this component keeps
   *  knowing nothing about PTZ — it draws a camera, and the caller decides
   *  what belongs on top of it. Currently only the focus dialog passes one:
   *  aiming a camera in a grid tile a few hundred pixels wide is guesswork. */
  overlayControls?: ReactNode;
}

// A dark scrim behind the bottom bar's text/controls so they stay legible
// over arbitrary video content, independent of the app's light/dark theme —
// this is why it is a fixed rgba() rather than a themed surface token; a
// themed color would go translucent-wrong the moment the underlying frame is
// bright. Single-direction fade since this bar only ever sits at the bottom
// edge now — the header below has its own solid treatment.
const scrimStyle = { background: "linear-gradient(rgba(0,0,0,0), rgba(0,0,0,.72))" };

// Measures the tile's own rendered width so the status panel below can drop
// its hint and Retry button on a tile too small to comfortably show them —
// a camera a few dozen pixels wide needs "Timed out", not a paragraph. A
// plain ResizeObserver rather than a CSS container query: this codebase
// already measures layout this way (useAutoGridColumns), and a query would
// need its own stylesheet this app has no mechanism to inject per-component.
function useElementWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export function VideoTile({
  camera,
  slots,
  enabled,
  autoStart = false,
  health,
  isFeedLive = false,
  tileId = camera.id,
  onOpenFocus,
  onRemoveFromWall,
  onManualPlay,
  onManualStop,
  canEvict = false,
  commands,
  onRequestSlot,
  isSlotRequested = false,
  overlayControls,
}: VideoTileProps) {
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  // Drives one reveal for the whole tile: hover or focus anywhere on it — the
  // header's own transport buttons included — shows every control at once,
  // rather than each control area tracking its own hover independently. The
  // 150ms delay keeps a cursor sweeping across the wall from lighting up
  // every tile it crosses. Spread onto plain elements below, not Astryx
  // layout components — Thumbnail (the one first-party component built on
  // this same hook) does the same, because the className this hands out
  // carries the actual reveal CSS and nothing here guarantees a layout
  // component forwards an incoming className to its root node.
  const { getContainerProps, getContentRevealProps } = useContainerReveal();
  const [fit, toggleFit] = useVideoFit(camera.id);
  const [tileRef, tileWidth] = useElementWidth<HTMLDivElement>();
  // Below the width where two 16:9 tiles no longer fit side by side on a
  // phone (see LiveGridPage's own MAX_NARROW_GRID_COLUMNS comment) — past
  // that point there's room for an icon and one line of title, nothing more.
  const isCompactTile = tileWidth > 0 && tileWidth < 220;
  const { state, videoRef, retry, play, playEvicting, pause, resume, stop, contention, dismissContention } =
    useHlsStream({
      cameraId: camera.id,
      tileId,
      slots,
      enabled,
      autoStart,
    });

  // Tracked independently of the player state machine on purpose: the stream
  // can be genuinely live (data flowing, confirmed by the backend and by
  // hls.js loading fragments) while the video element itself sits paused
  // because the browser's autoplay policy declined to start it without a
  // user gesture. Those are different facts, and conflating them is what
  // previously made a healthy stream look like a timeout — see useHlsStream.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setIsVideoPaused(false);
    const onPause = () => setIsVideoPaused(true);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    setIsVideoPaused(video.paused);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
    };
  }, [videoRef]);

  // play/stop/retry are recreated every render (they close over
  // cameraId/tileId inside useHlsStream), so anything below that needs the
  // latest one without re-running its own effect every render reads it
  // through here instead of depending on it directly.
  const actionsRef = useRef({ play, stop, retry });
  actionsRef.current = { play, stop, retry };

  // When somebody else stops this camera's stream, its segments simply stop
  // existing and hls.js reports a decode failure a few seconds later. That
  // presents a deliberate operator decision as a fault — a red "Repeated
  // failures" panel — so the tile is shut down cleanly the moment the order
  // arrives and shows "Not watching" instead. The toast (raised elsewhere,
  // addressed to the person rather than a tile) explains why.
  //
  // An approved request admits this camera server-side but starts nothing —
  // the requester still needs their own lease. Rather than making them press
  // play a second time after waiting for approval, the page orders a start
  // here.
  //
  // Ignored entirely while `enabled` is false. That flag is false for the
  // grid tile of a camera currently open in the focus dialog, and that
  // dialog's own tile holds the slot under a distinct tileId
  // (FOCUS_TILE_ID) — start() does not itself check `enabled`, so acting on
  // an order here while suspended would acquire a SECOND slot and lease for
  // one camera: two viewers on a shared budget, one of them invisible, and
  // a viewer_count that lies.
  useEffect(() => {
    if (!commands || !enabled) return;
    return commands.subscribe(camera.id, (command) => {
      if (command === "stop") actionsRef.current.stop();
      else actionsRef.current.play();
    });
  }, [commands, camera.id, enabled]);

  // What the SERVER says about this camera — a different question from the
  // local player state below, and folded into the panel copy only while
  // idle (see applyServerHealth's own comment on why). The header's own
  // "No signal" token, further down, is not gated on idle: local `playing`
  // + server `failed` is precisely the few seconds of warning before this
  // tile's own buffer runs out, and it is the single most valuable moment
  // this signal has.
  const serverHealth = describeServerHealth(health, isFeedLive);
  // isSlotRequested is passed through so a full-wall refusal for a camera
  // THIS browser has already asked about reads as "waiting for approval"
  // rather than a red dead end — see describePlayerState's own comment on
  // why. applyServerHealth then layers the server's own view on top, but
  // only while idle (see its own comment) — the two compose because they
  // answer different questions about different states.
  const presentation = applyServerHealth(describePlayerState(state, { isSlotRequested }), state, serverHealth);

  // Two cases where the server's own view should short-circuit a local
  // timer, rather than making the operator wait it out:
  //
  //  - Local `buffering`, server `failed`: the server already knows a pull
  //    died; waiting out useHlsStream's own local stall watchdog when this
  //    signal already says so just delays the same outcome. Deliberately
  //    gated on `buffering`, not `playing` — a `playing` tile with a
  //    `failed` server is the warning window the header's own "No signal"
  //    token above exists for (see this state's own comment), not yet the
  //    stuck state this retries out of.
  //  - Local `reconnecting`, server `ready`: this tile gave up and is
  //    waiting on useHlsStream's flat RECONNECT_RETRY_DELAY_MS (30s) timer,
  //    but the server has ALREADY recovered — on a flaky camera that stalls
  //    every minute or two, that 30s poll cadence is often enough to make a
  //    tile that IS retrying read as permanently stuck, because it spends
  //    most of its time waiting rather than watching. If the server already
  //    says the pull is healthy, there is nothing to wait for.
  //
  // hasRetriedRef is what stops this firing on every render while either
  // condition holds (serverHealth is a fresh object each render, and retry
  // itself is recreated every render too) — it fires once per occurrence and
  // re-arms only once the tile leaves that exact state. One ref covers both
  // cases safely: `state.kind` can only be `buffering` or `reconnecting` at
  // a time, never both, so at most one condition is ever true at once, and
  // a real state change always lands on an intermediate render (`starting`,
  // at minimum) where neither holds before the tile could reach the other.
  const hasRetriedForServerFailureRef = useRef(false);
  useEffect(() => {
    const isStuckBuffering = state.kind === "buffering" && serverHealth.kind === "failed";
    const isStaleReconnect = state.kind === "reconnecting" && serverHealth.kind === "ready";
    if (!isStuckBuffering && !isStaleReconnect) {
      hasRetriedForServerFailureRef.current = false;
      return;
    }
    if (hasRetriedForServerFailureRef.current) return;
    hasRetriedForServerFailureRef.current = true;
    actionsRef.current.retry();
  }, [state.kind, serverHealth.kind]);

  const isPaused = state.kind === "paused";
  // Is there a real, CURRENT frame from this camera on screen right now —
  // live or intentionally held? The status panel exists for when there is
  // nothing to look at, so its gate (showOverlay, below) is the exact
  // inverse of this. Buffering was carved out of the panel by hand for
  // exactly that reason — the header's pulsing "Live" status and LIVE token
  // already say the stream is still up, just momentarily stalled, so a
  // second, bigger "Buffering…" panel on top of a real frame read as a
  // broken tile catching fire rather than a healthy one catching up.
  // `paused` needs the identical carve-out for a stronger reason: pause
  // exists so an operator can hold a frame and read a plate, a face or a
  // gauge off it (see useHlsStream's own comment on pause), and that frame
  // lives in the middle of the tile — precisely where the panel used to be
  // drawn on top of it.
  const hasPicture = state.kind === "playing" || state.kind === "buffering" || isPaused;
  // The picture is at the LIVE EDGE: what's on screen is now, not held. Not
  // the same question as hasPicture — a paused tile still has a picture but
  // is not live, and collapsing "live" and "has a picture" into one flag is
  // what caused the paused-frame bug: the bottom bar and PTZ pad used to
  // gate on isLive alone, so pausing hid the fit toggle (more useful while
  // paused, not less — un-cropping is how you reach the edge of a frame a
  // plate sits in) at the very moment a panel covered the frame it froze.
  const isLive = state.kind === "playing" || state.kind === "buffering";
  // The literal complement of hasPicture, not a hand-maintained exclusion
  // list — a future state can no longer accidentally end up with both a
  // picture AND a panel drawn over it, which is exactly how `paused` was
  // missed after `buffering` was excluded for the identical reason above.
  const showOverlay = !hasPicture;
  const needsPlayGesture = isLive && isVideoPaused;

  // Transport controls. Only ever one play-or-pause button plus stop, so a
  // tile a few hundred pixels wide does not grow a control bar.
  //
  // Play is offered from idle, and from an error only when retrying could
  // actually help — a disabled camera or a codec this device cannot decode
  // stays broken however many times the button is pressed, and a control that
  // never works is worse than none. A queued tile gets no play button either:
  // it is already waiting its turn, and pressing play would just refuse the
  // slot again and land back where it started.
  // A full wall is not a fault to retry — canRetry stays false for it (see
  // playerCopy.ts) because pressing play again genuinely changes nothing
  // most of the time. RE-ASKING is a different question from retrying, and
  // it is the one this covers: the header's Play icon doubles as "ask for a
  // slot" while quota-blocked and not already requested, which matters most
  // on a compact tile too narrow for the status panel's own button below.
  // Also offered from `reconnecting`: a compact tile has no status-panel
  // Retry button at all (see below), so the header icon is its only manual
  // override of the automatic backoff.
  const isQuotaBlocked = state.kind === "error" && state.code === "quota_exhausted";
  const canPlay =
    state.kind === "idle" ||
    state.kind === "reconnecting" ||
    (state.kind === "error" && presentation.canRetry) ||
    (isQuotaBlocked && !isSlotRequested);
  const canPause = isLive;
  const canStop = state.kind !== "idle";

  // Everything that scans the wall at a glance stays lit; everything that
  // touches the stream waits for hover/focus. LIVE stays in the first group
  // on purpose — see the prop's own comment above.
  const transportControls = (
    <HStack gap={1} vAlign="center">
      {canPlay && (
        <IconButton
          label={`Start streaming ${camera.name}`}
          tooltip="Start streaming"
          icon={<Play size={14} />}
          variant="ghost"
          size="sm"
          onClick={() => {
            onManualPlay?.();
            if (state.kind === "idle") play();
            else retry();
          }}
        />
      )}
      {isPaused && (
        <IconButton
          label={`Resume ${camera.name}`}
          tooltip="Resume at the live edge"
          icon={<Play size={14} />}
          variant="ghost"
          size="sm"
          onClick={resume}
        />
      )}
      {canPause && (
        <IconButton
          label={`Pause ${camera.name}`}
          // Says plainly that this is not a way to save bandwidth. The
          // stream keeps arriving and the camera keeps being pulled over
          // the site link; only the picture is held.
          tooltip="Freeze the picture — the stream keeps running"
          icon={<Pause size={14} />}
          variant="ghost"
          size="sm"
          onClick={pause}
        />
      )}
      {canStop && (
        <IconButton
          label={`Stop streaming ${camera.name}`}
          tooltip="Stop streaming and free a slot"
          icon={<Square size={14} />}
          variant="ghost"
          size="sm"
          onClick={() => {
            onManualStop?.();
            stop();
          }}
        />
      )}
      {onOpenFocus && (
        <IconButton
          label={`View ${camera.name} fullscreen`}
          tooltip="View fullscreen"
          icon={<Maximize2 size={14} />}
          variant="ghost"
          size="sm"
          onClick={onOpenFocus}
        />
      )}
      {onRemoveFromWall && (
        <IconButton
          label={`Remove ${camera.name} from the wall`}
          tooltip="Remove from wall"
          icon={<X size={14} />}
          variant="ghost"
          size="sm"
          onClick={onRemoveFromWall}
        />
      )}
    </HStack>
  );

  return (
    <Card padding={0} elevation="low" style={{ overflow: "hidden" }}>
      <AspectRatio ratio={16 / 9} fit="cover">
        {/* One MediaTheme covers the video plus every overlay drawn over it:
            the header, the status overlay's Retry Button, the play-gesture
            button, and the bottom scrim's mute IconButton all get correct
            hover/pressed/focus tokens against this fixed-dark backdrop
            regardless of the app's own light/dark mode. */}
        <MediaTheme mode="dark">
          {/* getContainerProps() here (not on the header alone) makes
              hovering or focusing anywhere on the tile — video included —
              reveal the header's transport buttons and the bottom bar's mute
              button together, one reveal for the whole tile rather than
              several independent ones. */}
          <VStack
            ref={tileRef}
            {...getContainerProps({ hoverDelay: 150 })}
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              // Without this, `position: relative` with the default
              // `z-index: auto` creates NO stacking context of its own, so
              // this tile's z-indices (see tileChrome's TileLayer table)
              // would compete page-globally with Astryx's Dialog/Popover
              // portals instead of staying scoped to this one tile.
              isolation: "isolate",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- live camera feed, no track to caption */}
            <video
              ref={videoRef}
              muted={isMuted}
              playsInline
              {...tileLayer("video", {
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: fit,
                background: "var(--color-background-body)",
              })}
            />

            {/* Drawn inside the 16:9 frame, not stacked above it as its own
                row — that old layout cost every tile ~44-53px the
                auto-column solver never knew about (useAutoGridColumns
                models tiles as pure 16:9) and read as a row of cards rather
                than one wall. `var(--color-overlay)` is a fixed light-dark()
                pair meant for scrims over arbitrary media (Thumbnail's own
                remove-button panel uses the same token), not a themed
                surface color that would invert over a bright frame. Padding
                is step 1 (4px), not the design system's own default scrim
                padding (12px) — with two rows of chrome (this header, the
                bottom bar) already stacked in a tile a few hundred pixels
                tall, the token's own default reads as too tall here. */}
            <HStack
              {...tileLayer("header", { insetBlockStart: 0, insetInline: 0, background: "var(--color-overlay)" })}
              justify="between"
              vAlign="center"
              padding={1}
              gap={1.5}
            >
              <HStack gap={1.5} vAlign="center" style={{ minWidth: 0 }}>
                <StatusDot
                  variant={isLive ? "success" : isPaused ? "warning" : state.kind === "error" ? "error" : "neutral"}
                  label={isLive ? "Live" : isPaused ? "Paused — frame held" : state.kind === "error" ? "Error" : "Not live"}
                  // Paused is not a fault, but what's on screen is not now —
                  // on a wall scanned at a glance, that is the one thing a
                  // tile must never leave ambiguous, hence a variant of its
                  // own rather than folding it into "neutral".
                  tooltip={isPaused ? "The picture is frozen. The stream itself is still running." : undefined}
                  isPulsing={isLive}
                />
                {/* One line, name and location together — two stacked lines
                    of text alone were taller than the old header bar. Text's
                    own truncation tooltip covers the case a long combined
                    string gets cut off. */}
                <Text weight="semibold" maxLines={1}>
                  {camera.location ? `${camera.name} · ${camera.location}` : camera.name}
                </Text>
              </HStack>
              <HStack gap={1} vAlign="center">
                {/* Token, not Badge: this is a broadcast-style category label
                    ("this is a live feed"), not a count — Badge is reserved
                    for counts elsewhere in this app. "red" is a non-semantic
                    color, deliberately not "error": the pulsing StatusDot to
                    the left already carries the actual success/error/neutral
                    status. */}
                {isLive && <Token label="LIVE" color="red" size="sm" />}
                {/* Same broadcast-token treatment as LIVE, one at a time —
                    isLive and isPaused are mutually exclusive states of the
                    same player, never both true. */}
                {isPaused && <Token label="PAUSED" color="orange" size="sm" icon={<Pause size={10} />} />}
                {/* Not gated on local state, unlike LIVE/PAUSED above — this
                    answers a different question (is the SERVER pulling this
                    camera at all?) that can disagree with what this tile's
                    own player is doing. Shown only on `failed`, never on
                    every healthy tile: a mark that lights up on 25 fine
                    tiles is a mark nobody reads by the end of a shift; the
                    exception is what stays visible. */}
                {serverHealth.kind === "failed" && (
                  <Token label="No signal" color="red" size="sm" icon={<AlertTriangle size={10} />} />
                )}
                {/* Forced visible while paused: with the status panel gone
                    (see hasPicture/showOverlay above), the Resume button
                    right here is the tile's only way out of pause, and it
                    must not sit behind a 150ms hover dwell an operator has
                    no reason to expect on a frame they deliberately froze. */}
                <span
                  {...getContentRevealProps({
                    isLayoutPreserved: true,
                    forceVisibility: isPaused ? "shown" : undefined,
                  })}
                >
                  {transportControls}
                </span>
              </HStack>
            </HStack>

            {showOverlay && (
              <VStack
                {...tileLayer("statusPanel", {
                  // Insets from the header down, not the frame's own top
                  // edge — the header now paints ABOVE this layer (see the
                  // TileLayer table in tileChrome.ts), so a panel centred
                  // over the full frame would have its top edge land inside
                  // the header strip on a short tile and clip against it.
                  insetBlockStart: TILE_HEADER_CLEARANCE,
                  insetInlineStart: 0,
                  insetInlineEnd: 0,
                  insetBlockEnd: 0,
                })}
                vAlign="center"
                hAlign="center"
                padding={2}
              >
                {/* A surface of its own, not text floating on the last video
                    frame — `presentation.tone` was computed by playerCopy
                    for every error and discarded until now, which is why a
                    timeout used to read as an empty box with three small
                    lines of text rather than an error. Compact tiles get the
                    icon/spinner and one line of title only: a camera a few
                    dozen pixels wide has no room for a hint paragraph and a
                    Retry button, and doesn't need one — the header's own
                    Play icon (revealed on hover/focus) already retries.

                    `tileLayerContentStyle` re-enables the pointer here: the
                    wrapper above is `pointer-events: none` (it is a passive
                    full-frame layer, see tileChrome.ts), but this card is the
                    one thing inside it that must take clicks — it holds the
                    Retry button below. */}
                <VStack
                  style={{
                    ...tileLayerContentStyle,
                    background: presentation.tone === "danger" ? "var(--color-error-muted)" : "var(--color-overlay)",
                    borderRadius: "var(--radius-container)",
                    maxWidth: "100%",
                  }}
                  vAlign="center"
                  hAlign="center"
                  gap={1.5}
                  padding={isCompactTile ? 1.5 : 3}
                >
                  {presentation.tone === "danger" ? (
                    <AlertTriangle size={isCompactTile ? 16 : 20} color="var(--color-error)" />
                  ) : (
                    presentation.showSpinner && <Spinner shade="onMedia" size={isCompactTile ? "sm" : "md"} />
                  )}
                  <Text weight="medium" justify="center" maxLines={isCompactTile ? 1 : 2}>
                    {presentation.title}
                  </Text>
                  {!isCompactTile && presentation.hint && (
                    <Text type="supporting" justify="center" maxLines={2}>
                      {presentation.hint}
                    </Text>
                  )}
                  {!isCompactTile && presentation.canRetry && (
                    <Button label="Retry" size="sm" variant="secondary" onClick={retry} />
                  )}
                  {/* A full wall is not a fault to retry — it's a negotiation
                      to (re)open. Re-asks the server rather than reopening a
                      cached copy of the earlier refusal: the refusal named a
                      candidate at a moment, the server refuses a candidate
                      that has since stopped rather than substituting
                      whatever took its place, and capacity frees WITHOUT any
                      notice being published for it — an ordinary Stop
                      publishes nothing, unlike an eviction. Re-asking is the
                      only way this browser can discover freed capacity; if
                      the wall is still full, the refusal that comes back
                      carries a current candidate and re-raises this same
                      dialog with it. onManualPlay clears `suppressed` first,
                      the same as the header's own Play button — this camera
                      may have been evicted earlier in the session, which
                      would otherwise silently swallow the ask. */}
                  {!isCompactTile && isQuotaBlocked && !isSlotRequested && (
                    <Button
                      label={canEvict ? "Make room…" : "Ask for a slot"}
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        onManualPlay?.();
                        play();
                      }}
                    />
                  )}
                </VStack>
              </VStack>
            )}

            {/* A ring, not a panel — the status panel is gone for `paused`
                on purpose (see hasPicture above), so a frozen frame needs a
                mark that doesn't cost the picture it's marking. On a 3x3
                wall the header's PAUSED token is a small fraction of the
                tile; a frame that reads as live when it is not is the most
                dangerous thing this UI can show, so the mark scales with the
                tile itself. `aria-hidden`: purely decorative, redundant with
                the header's StatusDot label and tooltip for anyone using a
                screen reader. Its own layer rather than a box-shadow on the
                container VStack — an inset shadow paints with the element's
                own background, and every absolutely-positioned child
                (the `<video>` included) would then paint over it. */}
            {isPaused && (
              <VStack
                aria-hidden
                {...tileLayer("pausedFrame", {
                  inset: 0,
                  boxShadow: "inset 0 0 0 var(--spacing-0-5) var(--color-warning)",
                  borderRadius: "inherit",
                })}
              />
            )}

            {needsPlayGesture && (
              // Stays a literal white, unlike the panels above: the Play
              // icon below is a plain lucide SVG using fill="currentColor",
              // real CSS color inheritance rather than an Astryx token
              // MediaTheme could resolve on its own.
              //
              // A real full-frame click target, not a passive layer — the
              // `playGesture` entry in tileChrome's TileLayer table marks it
              // as a pointer target, so `tileLayer` leaves pointer-events at
              // its default (auto) rather than forcing `none`.
              <VStack
                as="button"
                {...tileLayer("playGesture", {
                  inset: 0,
                  color: "white",
                  background: "rgba(0,0,0,.35)",
                  border: "none",
                  cursor: "pointer",
                })}
                vAlign="center"
                hAlign="center"
                gap={1}
                onClick={() => videoRef.current?.play().catch(() => {})}
              >
                <Play size={32} fill="currentColor" />
                <Text type="supporting" color="inherit">
                  Click to play
                </Text>
              </VStack>
            )}

            {/* Only while genuinely live AND actually visible: a control pad
                over a frozen or black tile invites an operator to aim a
                camera they cannot currently see, which is how a camera ends
                up pointed at a wall with nobody realising for a shift. That
                includes the play-gesture moment — `isLive` alone is not
                enough: while `needsPlayGesture` is true, the frame is still
                black to this viewer even though the stream is up for
                everyone else. The fix is not to bury the pad under the
                play-gesture layer (it would still be reachable by keyboard);
                it is to not render it until there is something to aim. */}
            {isLive && !needsPlayGesture && overlayControls && (
              <VStack {...tileLayer("ptzPad", ptzOverlayStyle)} padding={2} gap={1}>
                {overlayControls}
              </VStack>
            )}

            {/* hasPicture, not isLive: the fit toggle is MORE useful paused
                than live — un-cropping is how an operator reaches the edge
                of a frame a plate or gauge sits in — and the kbps/watching
                line stays true, since pausing deliberately keeps the pull
                running (see useHlsStream's own comment on what pause means). */}
            {hasPicture && (
              <HStack
                {...tileLayer("bottomBar", { insetBlockEnd: 0, insetInline: 0, ...scrimStyle })}
                justify="between"
                vAlign="center"
                padding={2}
                gap={2}
              >
                <Text type="supporting">
                  {health ? `${health.bitrate_kbps} kbps · ${health.viewer_count} watching` : ""}
                </Text>
                <span {...getContentRevealProps({ isLayoutPreserved: true })}>
                  <HStack gap={1} vAlign="center">
                    {/* Per-camera, not a single app-wide setting — see
                        useVideoFit's own comment on why. */}
                    <IconButton
                      label={fit === "cover" ? "Show full frame" : "Fill frame"}
                      tooltip={fit === "cover" ? "Show full frame (letterboxed)" : "Fill frame (cropped)"}
                      icon={fit === "cover" ? <Shrink size={14} /> : <Expand size={14} />}
                      variant="ghost"
                      size="sm"
                      onClick={toggleFit}
                    />
                    <IconButton
                      label={isMuted ? "Unmute" : "Mute"}
                      tooltip={isMuted ? "Unmute" : "Mute"}
                      icon={isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMuted((m) => !m)}
                    />
                  </HStack>
                </span>
              </HStack>
            )}
          </VStack>
        </MediaTheme>
      </AspectRatio>
      {/* Rendered only while there IS a contention, not merely toggled with
          isOpen: Astryx's Dialog renders its children regardless of isOpen, so
          a permanently-mounted one would keep a stale refusal (and its camera
          name) alive for the rest of the session. */}
      {contention && (
        <StreamContentionDialog
          isOpen
          onOpenChange={(open) => {
            if (!open) dismissContention();
          }}
          cameraName={camera.name}
          contention={contention}
          isConfirming={state.kind === "starting"}
          onConfirm={
            canEvict && contention.candidate
              ? () => {
                  const candidateId = contention.candidate?.camera_id;
                  if (candidateId) playEvicting(candidateId);
                }
              : undefined
          }
          isRequested={isSlotRequested}
          onRequest={
            !canEvict && contention.candidate && onRequestSlot
              ? () => {
                  const candidateId = contention.candidate?.camera_id;
                  // Returned, not `void`-ed: the dialog awaits this to show
                  // its own pending state and to catch a failure in front of
                  // the person who asked. Voiding it here is what used to
                  // turn a failed request into a silent, unhandled
                  // rejection — the dialog kept offering "Ask for a slot" as
                  // if nothing had happened.
                  return candidateId ? onRequestSlot(candidateId) : Promise.resolve();
                }
              : undefined
          }
        />
      )}
    </Card>
  );
}
